import React, { useState } from "react";
import Logger from "../../../services/Logger";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Paper from "@mui/material/Paper";

const CreateFrenchPuzzlesDialog = ({ open, onClose, puzzleType, onExecute }) => {
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const handleExecute = async () => {
        try {
            setLoading(true);
            setError(null);
            setResult(null);
            
            const response = await onExecute();
            setResult(response);
        } catch (err) {
            Logger.error("Error creating French puzzles:", err);
            setError(err.message || "Failed to create French puzzles");
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setResult(null);
            setError(null);
            onClose();
        }
    };

    const puzzleTypeLabel = puzzleType === "grid" ? "Grid" : "Crossword";

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                Create French Puzzles - {puzzleTypeLabel}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                        This will create French versions based on ready-for-review English {puzzleTypeLabel.toLowerCase()} puzzles. Only English puzzles that don't already have French versions will be processed.
                    </Typography>
                </Box>

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
                        <CircularProgress />
                        <Typography variant="body2" sx={{ ml: 2 }}>
                            Creating French puzzles...
                        </Typography>
                    </Box>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {result && !loading && (
                    <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                            Results
                        </Typography>
                        <Typography variant="body2" component="div" sx={{ pl: 0 }}>
                            • Total: {result.totalCount ?? 0}<br />
                            • Created: {result.createdCount ?? 0}<br />
                            • Skipped: {result.skippedCount ?? 0}
                            {result.errors && result.errors.length > 0 && (
                                <>
                                    <br />• Errors: {result.errors.length}
                                    {result.errors.map((msg, idx) => (
                                        <Typography key={idx} variant="caption" component="div" sx={{ pl: 2, color: 'error.main' }}>
                                            - {msg}
                                        </Typography>
                                    ))}
                                </>
                            )}
                        </Typography>
                    </Paper>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    {result ? "Close" : "Cancel"}
                </Button>
                {!result && (
                    <Button
                        onClick={handleExecute}
                        variant="contained"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <CircularProgress size={16} sx={{ mr: 1 }} />
                                Processing...
                            </>
                        ) : (
                            "Create"
                        )}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default CreateFrenchPuzzlesDialog;
